












































































































































import { Component, Prop, Vue, Ref, Watch } from "vue-property-decorator";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import TaxonomyIcon, { TAXONOMY_ICON_SET } from "@/components/TaxonomyIcon.vue";

/**
 * Simulates a Stack in order to process a Nested Tree structure
 * in a iterative way without getting a stack overflow.
 */
interface VirtualStackItem {
  option: MultiLevelObject;
  parentItem: VirtualStackItem | null;
  visited: boolean;
  filteredChildren: MultiLevelObject[];
}

const DEBOUNCE_SEARCH_TIME = 320;

@Component({
  name: "multi-level-dropdown-v2",
  components: { TaxonomyIcon },
})
export default class MultiLevelDropdownV2 extends Vue {
  @Prop() readonly options!: Array<MultiLevelObject>;
  @Prop({ default: false }) readonly nested?: boolean;
  @Prop({ type: Array, default: () => [] }) readonly headers?: Array<string>;
  @Prop({ default: 0 }) readonly level?: number;
  @Prop({ default: false }) readonly multiple?: boolean;
  @Prop() readonly parent?: MultiLevelObject;
  @Prop({ default: false }) readonly checkedIcon!: boolean;
  @Prop({ type: Boolean, default: false }) readonly searchEnabled!: boolean;
  @Prop({ default: null }) iconSet?: string;
  @Prop({ default: null }) searchProp?: string;

  @Ref("dropdownChildren") readonly dropdownChildren!: MultiLevelDropdownV2[];
  @Ref("searchInput") searchInput!: HTMLInputElement;

  /**
   * Watcher that updates the recursive MultiLevelDropdownV2 instances `search` property
   * with the value passed by the original search input, this will trigger the `search` watcher
   * and permit the correct hydration until the last child.
   *
   * @param newVal <string | null>
   */
  @Watch("searchProp", { immediate: true })
  onSearchPropChanged(newVal: string | null) {
    this.search = newVal;
  }

  @Watch("search")
  onSearchUpdated(newVal: string | null) {
    this.handleSearchUpdate(newVal);
  }

  @Watch("options", { immediate: true, deep: true })
  onOptionsUpdated() {
    this.filteredOptions = this.filterOptions(this.search, this.options);
  }

  get taxonomyIconSet() {
    return TAXONOMY_ICON_SET;
  }

  get isTaxonomyContext() {
    return this.iconSet == this.taxonomyIconSet;
  }
  filteredOptions: MultiLevelObject[] = [];
  selected: MultiLevelObject | null = null;
  search: string | null = null;
  accordionOptionOpen: string[] = [];
  searchTimeout: number | null = null;

  get header() {
    const level = this.level || 0;

    if (this.headers && this.headers.length) {
      return this.headers[level];
    } else {
      return null;
    }
  }

  get subLevel() {
    const level = this.level || 0;
    return level + 1;
  }

  get dropdownClass() {
    return [
      {
        "multilevel-select__dropdown--nested": this.nested,
        "multilevel-select__dropdown": !this.nested,
        "multilevel-select__dropdown--with-header": this.header,
      },
    ];
  }

  get searchInputIconClass() {
    return {
      "lens-icon": !this.search,
      "cancel-input-icon": this.search,
    };
  }

  get dynamicCssHeightClass() {
    return this.searchEnabled
      ? "height: calc(100% - 8rem);"
      : "height: calc(100% - 3rem);";
  }

  get canFocusSearch() {
    return this.searchEnabled && !this.nested && this.searchInput;
  }

  get isSearchActive() {
    return this.search && this.search.trim().length >= 1;
  }

  get resultsFound() {
    return this.filteredOptions.length > 0;
  }

  created() {
    this.options.forEach((option) => {
      if (option.selected && option.children !== undefined) {
        this.updateChildren(option);
      }
    });
  }

  beforeDestroy() {
    this.clearSearchTimeout();
  }

  handleSearchUpdate(val: string | null) {
    this.clearSearchTimeout();

    if (!val || val.trim().length < 1) {
      this.handleEmptySearch();
      return;
    }

    this.handleDebounceSearch(val);
  }

  clearSearchTimeout() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  filterOptions(
    term: string | null,
    options: MultiLevelObject[]
  ): MultiLevelObject[] {
    if (!term || term.trim().length < 1 || !options || options.length === 0) {
      return options;
    }

    const cleanTerm = term.toLowerCase().trim();

    return this.performFilter(cleanTerm, options);
  }

  matchesText(option: MultiLevelObject, cleanTerm: string): boolean {
    return option.text ? option.text.toLowerCase().includes(cleanTerm) : false;
  }

  /**
   * Returns a VirtualStackItem array built from the MultiLevelObject[] options
   * in a bottom-up way, starting from more nested level to the
   * highest to preserve the correct items tree structure.
   * @param options The array of options to process.
   * @param parentItem Optional parent link for nested children.
   * @returns a list of VirtualStackItem useful to iterate and filter the options.
   */
  populateStack(
    options: MultiLevelObject[],
    parentItem: VirtualStackItem | null = null
  ): VirtualStackItem[] {
    const stackItems: VirtualStackItem[] = [];

    for (let i = options.length - 1; i >= 0; i--) {
      stackItems.push({
        option: options[i],
        parentItem: parentItem,
        visited: false,
        filteredChildren: [],
      });
    }

    return stackItems;
  }

  processFirstVisit(
    current: VirtualStackItem,
    stack: VirtualStackItem[]
  ): void {
    current.visited = true;

    if (current.option.children && current.option.children.length > 0) {
      const childrenItems = this.populateStack(
        current.option.children,
        current
      );

      stack.push(...childrenItems);
    }
  }

  evaluateNode(
    current: VirtualStackItem,
    cleanTerm: string,
    filteredResults: MultiLevelObject[]
  ): void {
    const matches = this.matchesText(current.option, cleanTerm);
    const hasMatchingChildren = current.filteredChildren.length > 0;

    if (matches || hasMatchingChildren) {
      const clonedOption: MultiLevelObject = { ...current.option };

      clonedOption._original = current.option._original || current.option;

      if (current.option.children) {
        clonedOption.children = current.filteredChildren;
      }

      if (!this.accordionOptionOpen.includes(current.option.id)) {
        this.openAccordion(current.option.id);
      }

      if (current.parentItem) {
        current.parentItem.filteredChildren.push(clonedOption);
      } else {
        filteredResults.push(clonedOption);
      }
    }
  }

  performFilter(
    cleanTerm: string,
    options: MultiLevelObject[]
  ): MultiLevelObject[] {
    const filteredResults: MultiLevelObject[] = [];
    const stack: VirtualStackItem[] = this.populateStack(options);

    while (stack.length > 0) {
      const current = stack[stack.length - 1];

      if (!current.visited) {
        this.processFirstVisit(current, stack);
      } else {
        stack.pop(); // removing from stack
        this.evaluateNode(current, cleanTerm, filteredResults);
      }
    }

    return filteredResults;
  }

  handleEmptySearch() {
    this.filteredOptions = this.filterOptions(null, this.options);
    this.accordionOptionOpen = [];
  }

  handleDebounceSearch(val: string) {
    this.clearSearchTimeout();

    this.searchTimeout = window.setTimeout(() => {
      this.accordionOptionOpen = [];
      this.filteredOptions = this.filterOptions(val, this.options);

      this.searchTimeout = null;
    }, DEBOUNCE_SEARCH_TIME);
  }

  onClickCheckbox(option: MultiLevelObject) {
    this.select(option);
  }

  isOptionOpen(option: MultiLevelObject) {
    return this.accordionOptionOpen.includes(option.id);
  }

  indentationAmount(level: number) {
    return {
      paddingLeft: `calc(${level * 0.5}rem)`,
    };
  }

  backgroundColorByLevel(level: number) {
    switch (level) {
      case 0:
        return "sector-item-bg-color";
      case 1:
        return "category-item-bg-color";
      case 2:
        return "class-item-bg-color";
      default:
        return "";
    }
  }

  /**
   * @param option: MultiLevelObject option.
   * @description Keeps the status of the current opened options accordion
   * in the list, using the id.
   **/
  handleAccordionOptionsSpread(option: MultiLevelObject) {
    const index = this.accordionOptionOpen.indexOf(option.id);
    if (index > -1) {
      this.closeAccordion(index);
    } else {
      this.openAccordion(option.id);
    }
  }

  closeAccordion(index: number) {
    this.accordionOptionOpen.splice(index, 1);
  }

  openAccordion(optionId: string) {
    this.accordionOptionOpen.push(optionId);
  }

  onToggleAccordion(option: MultiLevelObject) {
    this.handleAccordionOptionsSpread(option);
  }

  onClickItem(option: MultiLevelObject) {
    if (this.multiple) {
      this.handleAccordionOptionsSpread(option);
    } else {
      this.select(option);
    }
  }

  hasSubLevel(option: MultiLevelObject) {
    return option?.children?.length;
  }

  isNotSelectable(option: MultiLevelObject) {
    return option.locked || (this.multiple && option.denyMultiselect);
  }

  select(option: MultiLevelObject) {
    const target = option._original || option;

    if (!target || this.isNotSelectable(target)) return;

    if (this.multiple) {
      if (target.partial) {
        this.$set(target, "selected", true);
        this.$set(target, "partial", false);
      } else {
        this.$set(target, "selected", !target.selected);
      }

      this.updateChildren(target);
    }

    this.$emit("selected", target, this.level);
  }

  clearSearch() {
    this.search = null;
  }

  updateChildren(option: MultiLevelObject) {
    const children: MultiLevelObject[] = option.children || [];
    const isParentSelected = !!option.selected;

    for (const child of children) {
      this.$set(child, "selected", isParentSelected);
      this.$set(child, "partial", false);
      this.updateChildren(child);
    }
  }

  updatePartial() {
    for (const option of this.options) {
      const target = option._original || option;

      if (target.children && target.children.length > 0) {
        let selectedCount = 0;
        let partialCount = 0;
        const totalChildren = target.children.length;

        for (const child of target.children) {
          if (child.selected) selectedCount++;
          if (child.partial) partialCount++;
        }

        if (selectedCount === 0) {
          this.$set(target, "selected", false);
          this.$set(target, "partial", partialCount > 0);
        } else if (selectedCount < totalChildren) {
          this.$set(target, "selected", false);
          this.$set(target, "partial", true);
        } else {
          this.$set(target, "selected", true);
          this.$set(target, "partial", false);
        }
      }
    }
  }

  getSelectedOptions(options: MultiLevelObject[], level: number) {
    let selected: {
      [key: number]: string[];
    } = {};

    selected[level] = [];

    options.forEach((option) => {
      if (option.selected) {
        selected[level].push(option.id);
      }

      if (option.children && option.children.length) {
        selected = {
          ...selected,
          ...this.getSelectedOptions(option.children, level + 1),
        };
      }
    });

    return selected;
  }

  onNestedSelected(option: MultiLevelObject, level: number) {
    if (this.multiple) {
      this.updatePartial();
    }

    this.$emit("selected", option, level);
  }

  /**
   * This method is called by the parent component MultiLevelSelectV2.vue
   * It set the search automatically on focus when the parent opens the first
   * not nested dropdown.
   *
   * @returns void;
   */
  focusSearch(): void {
    this.$nextTick(() => {
      if (this.canFocusSearch) {
        this.searchInput.focus({ preventScroll: false });
      }
    });
  }

  closeAllAccordionsOptions() {
    this.search = null;
    this.accordionOptionOpen = [];

    if (this.dropdownChildren && this.dropdownChildren.length > 0) {
      this.dropdownChildren.forEach((child) => {
        child.closeAllAccordionsOptions();
      });
    }
  }
}
